import React, { useState, useEffect } from 'react';
import { ChevronRight, Syringe, Pill, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [hoveredId, setHoveredId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (index) => {
    return index % 2 === 0 ? Syringe : Pill;
  };

  if (loading) {
    return (
      <section id="products" className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 border-4 border-blue-900 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-blue-900 font-medium">Loading products...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="products" className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-20 right-10 w-96 h-96 bg-blue-200 rounded-full filter blur-3xl opacity-10 animate-float"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-sky-200 rounded-full filter blur-3xl opacity-10 animate-float-delayed"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full mb-6 animate-bounce-in">
            <Sparkles className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-semibold text-blue-900">Premium Quality</span>
          </div>
          <h2 className="text-5xl sm:text-6xl font-bold text-blue-900 mb-6 animate-slide-up">
            Our Products
          </h2>
          <div className="w-24 h-1 animated-gradient-border mx-auto mb-8 rounded-full"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-slide-up stagger-1">
            Pharmaceutical-grade bio-peptides designed for optimal results
          </p>
        </div>

        <div className="space-y-6">
          {products.map((product, index) => {
            const Icon = getIcon(index);
            const isHovered = hoveredId === product._id;

            return (
              <div
                key={product._id}
                onMouseEnter={() => setHoveredId(product._id)}
                onMouseLeave={() => setHoveredId(null)}
                className={`group relative bg-white rounded-2xl border-2 transition-all duration-500 cursor-pointer overflow-hidden ${
                  isHovered
                    ? 'border-blue-500 shadow-2xl transform scale-[1.02] -rotate-1'
                    : 'border-blue-100 shadow-md hover:shadow-xl'
                }`}
                style={{
                  transformStyle: 'preserve-3d',
                  perspective: '1000px'
                }}
              >
                {/* Animated background gradient on hover */}
                <div
                  className={`absolute inset-0 bg-gradient-to-r from-blue-50 via-sky-50 to-blue-50 transition-opacity duration-500 ${
                    isHovered ? 'opacity-100' : 'opacity-0'
                  }`}
                ></div>

                {/* Shine effect overlay */}
                <div className={`absolute inset-0 transition-opacity duration-500 ${
                  isHovered ? 'opacity-100' : 'opacity-0'
                }`}>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
                </div>

                <div className="relative p-8 flex items-start gap-6">
                  {/* Product Image or Icon */}
                  <div className="relative flex-shrink-0">
                    {product.image_url ? (
                      // Show product image if available
                      <div 
                        className={`w-24 h-24 rounded-2xl overflow-hidden transition-all duration-500 cursor-pointer ${
                          isHovered ? 'scale-110 rotate-3 shadow-2xl' : 'shadow-md'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImage({ url: product.image_url, name: product.name });
                        }}
                        title="Click to view full size"
                      >
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                        {/* Fallback icon if image fails to load */}
                        <div style={{display: 'none'}} className={`w-24 h-24 rounded-2xl flex items-center justify-center bg-blue-100`}>
                          <Icon className="w-12 h-12 text-blue-900" />
                        </div>
                      </div>
                    ) : (
                      // Show icon if no image
                      <div
                        className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-500 ${
                          isHovered
                            ? 'bg-blue-900 scale-110 rotate-12 shadow-lg'
                            : 'bg-blue-100 group-hover:bg-blue-200'
                        }`}
                      >
                        <Icon
                          className={`w-8 h-8 transition-all duration-500 ${
                            isHovered ? 'text-white scale-110' : 'text-blue-900'
                          }`}
                        />
                      </div>
                    )}
                    {/* Glow ring */}
                    {isHovered && (
                      <div className="absolute inset-0 rounded-2xl bg-blue-500 opacity-50 blur-xl animate-pulse"></div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className={`text-2xl font-bold mb-3 transition-all duration-300 ${
                      isHovered ? 'text-blue-700 scale-105' : 'text-blue-900'
                    }`}>
                      {product.name}
                    </h3>
                    <p
                      className={`leading-relaxed transition-all duration-500 ${
                        isHovered ? 'text-gray-800 font-medium' : 'text-gray-600'
                      }`}
                    >
                      {product.description}
                    </p>
                  </div>

                  {/* Animated Arrow indicator */}
                  <div className="flex-shrink-0 relative">
                    <ChevronRight
                      className={`w-6 h-6 text-blue-600 transition-all duration-500 ${
                        isHovered ? 'transform translate-x-2 scale-125' : ''
                      }`}
                    />
                    {/* Arrow trail effect */}
                    {isHovered && (
                      <>
                        <ChevronRight className="w-6 h-6 text-blue-400 absolute top-0 left-0 opacity-50 transform -translate-x-2" />
                        <ChevronRight className="w-6 h-6 text-blue-300 absolute top-0 left-0 opacity-25 transform -translate-x-4" />
                      </>
                    )}
                  </div>
                </div>

                {/* Animated bottom accent line */}
                <div
                  className={`h-1 transition-all duration-500 ${
                    isHovered ? 'animated-gradient-border opacity-100' : 'bg-gray-200 opacity-0'
                  }`}
                ></div>

                {/* Corner decoration */}
                <div className={`absolute top-4 right-4 transition-all duration-500 ${
                  isHovered ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
                }`}>
                  <div className="w-3 h-3 bg-blue-500 rounded-full animate-ping"></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Enhanced Additional info */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-2xl border-2 border-blue-100 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <p className="text-gray-700 font-medium">
              All products manufactured to international pharmaceutical standards
            </p>
          </div>
        </div>
      </div>

      {/* Image Modal/Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm animate-fade-in"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white text-2xl font-bold transition-all duration-300 hover:scale-110 hover:rotate-90"
            onClick={() => setSelectedImage(null)}
            aria-label="Close"
          >
            ×
          </button>
          
          <div className="relative max-w-6xl max-h-[90vh] p-4 animate-scale-in">
            <img 
              src={selectedImage.url} 
              alt={selectedImage.name}
              className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="mt-4 text-center">
              <p className="text-white text-lg font-semibold bg-black/50 px-6 py-3 rounded-full inline-block backdrop-blur-sm">
                {selectedImage.name}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Products;
